/**
 * Görsel Üretim Paneli — Soru Stüdyosu Sağ Tepsisi
 *
 * Bu panel gerçek bir sağlayıcı bağlantısı IÇERMEZ.
 * Sağlayıcı yoksa tüm eylem butonları disabled ve neden görünür.
 *
 * KATÎ KURALLAR:
 *   - Üretim eylemi doğrudan DB'ye YAZAMAZ (candidate_only sözleşmesi).
 *   - Gerçek provider bağlandığında _buildGenerationJob() ile iş talebi oluşturulur.
 *   - "Bu soruda dene" (onTryOnQuestion) ve "Taslağa bağla" (onAttachToDraft) ayrı callback'lerdir.
 *   - Her iki callback'e null değil, doldurulmuş job nesnesi gönderilir.
 *   - Eski tam genişlik Media V2 ekranını etkilemez.
 *
 * Props:
 *   provider         {null|Object}   Bağlı sağlayıcı sözleşmesi. null = bağlı değil.
 *   onTryOnQuestion  {Function}      (job: GenerationJob) => void  — "Bu soruda dene"
 *   onAttachToDraft  {Function}      (job: GenerationJob) => void  — "Taslağa bağla"
 */

import { useState } from "react";
import { Sparkles, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import {
  GENERATABLE_USAGE_KEYS,
  GENERATION_RESULT_CONTRACT,
  PROVIDER_STATUS,
  GENERATION_ACTION,
} from "./visualGenerationModel.mjs";

const OPTION_KEYS = ['A', 'B', 'C', 'D'];

const KEY_LABELS = {
  'question.primary_visual':     'Soru Görseli',
  'question.gif':                'Soru GIF',
  'question.explanation_visual': 'Açıklama Görseli',
  'option.visual':               'Şık Görseli',
};

/**
 * Provider'dan bağımsız üretim iş talebi oluşturur.
 * Sağlayıcı bu nesneyi alır, üretimi yapar ve playback_url ekler.
 * Sonuç her zaman candidate_only sözleşmesiyle döner.
 *
 * @returns {import('./visualGenerationModel.mjs').GenerationJob}
 */
function _buildGenerationJob({ usageKey, optionKey, provider, action }) {
  return {
    contract:      GENERATION_RESULT_CONTRACT,   // 'candidate_only' — değiştirilemez
    action,                                       // GENERATION_ACTION.TRY_ON_QUESTION | ATTACH_TO_DRAFT
    usage_key:     usageKey,
    option_key:    optionKey ?? null,
    provider_name: provider?.name ?? null,
    requested_at:  new Date().toISOString(),
    // playback_url: sağlayıcı tamamladığında doldurulur
  };
}

export default function VisualGenerationPanel({ provider = null, onTryOnQuestion, onAttachToDraft }) {
  const [expanded, setExpanded] = useState(false);
  const [usageKey, setUsageKey] = useState('question.primary_visual');
  const [optionKey, setOptionKey] = useState('A');

  const isConnected  = provider?.status === PROVIDER_STATUS.CONNECTED;
  const providerName = provider?.name ?? null;
  const isOptionVisual = usageKey === 'option.visual';

  function handleTryOnQuestion() {
    if (!isConnected || !onTryOnQuestion) return;
    const job = _buildGenerationJob({
      usageKey,
      optionKey: isOptionVisual ? optionKey : null,
      provider,
      action: GENERATION_ACTION.TRY_ON_QUESTION,
    });
    onTryOnQuestion(job);
  }

  function handleAttachToDraft() {
    if (!isConnected || !onAttachToDraft) return;
    const job = _buildGenerationJob({
      usageKey,
      optionKey: isOptionVisual ? optionKey : null,
      provider,
      action: GENERATION_ACTION.ATTACH_TO_DRAFT,
    });
    onAttachToDraft(job);
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="visual-generation-panel">
      {/* Başlık */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid="vgp-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-violet-400" />
          <span className="text-xs font-semibold text-slate-600">Görsel Üretim</span>
          {!isConnected && (
            <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">
              Sağlayıcı yok
            </span>
          )}
          {isConnected && (
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
              {providerName}
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp size={12} className="text-slate-400" />
          : <ChevronDown size={12} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-slate-100 space-y-3 bg-white">

          {/* Sağlayıcı yok uyarısı */}
          {!isConnected && (
            <div
              className="flex gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg"
              data-testid="vgp-no-provider-notice"
            >
              <AlertCircle size={13} className="text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-semibold text-slate-600">Sağlayıcı bağlı değil</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Görsel üretimi için bir AI sağlayıcısının bu stüdyoya bağlanması gerekir.
                  Bağlantı sağlandığında bu panel aktif olur.
                </p>
              </div>
            </div>
          )}

          {/* Hedef seçimi */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
              Hedef
            </label>
            <select
              value={usageKey}
              onChange={e => setUsageKey(e.target.value)}
              disabled={!isConnected}
              className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 disabled:opacity-50 disabled:bg-slate-50"
              data-testid="vgp-usage-key-select"
            >
              {GENERATABLE_USAGE_KEYS.map(k => (
                <option key={k} value={k}>{KEY_LABELS[k] ?? k}</option>
              ))}
            </select>
            {isOptionVisual && (
              <select
                value={optionKey}
                onChange={e => setOptionKey(e.target.value)}
                disabled={!isConnected}
                className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 disabled:opacity-50 disabled:bg-slate-50"
                data-testid="vgp-option-key-select"
              >
                {OPTION_KEYS.map(k => (
                  <option key={k} value={k}>Şık {k}</option>
                ))}
              </select>
            )}
          </div>

          {/* Sonuç sözleşmesi notu */}
          <p className="text-[10px] text-slate-400">
            Sonuç türü:{' '}
            <span className="font-mono text-slate-500">{GENERATION_RESULT_CONTRACT}</span>
            {' '}— üretilen görsel doğrudan yayınlanmaz, aday olarak döner.
          </p>

          {/* Eylem butonları */}
          <div className="space-y-2">
            <button
              type="button"
              disabled={!isConnected}
              onClick={handleTryOnQuestion}
              className="w-full flex items-center justify-center gap-1.5 py-2 bg-violet-600 text-white text-xs rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="vgp-try-btn"
            >
              <Sparkles size={11} />
              Üret ve Bu Soruda Dene
            </button>
            <button
              type="button"
              disabled={!isConnected}
              onClick={handleAttachToDraft}
              className="w-full flex items-center justify-center gap-1.5 py-2 border border-violet-200 text-violet-600 text-xs rounded-lg hover:bg-violet-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="vgp-attach-btn"
            >
              Taslağa Bağla / Değiştir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
